/**
 * GR SOV Tab — displayed on the Project Detail page.
 *
 * Shows General Requirements Schedule of Values lines (GR-category
 * commitmentLineItems on the owner prime contract) with approved/remaining
 * variance. PM/admin can add or edit lines.
 *
 * WI-GR-03 frontend.
 */
import { Pencil, Plus, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCommitments } from "@/hooks/use-commitments";
import { useCostCodes } from "@/hooks/use-cost-codes";
import type { GrSovLine, GrSovLineInput } from "@/hooks/use-gr-sov";
import { useGrSov, useGrVariance, useUpdateGrSov } from "@/hooks/use-gr-sov";
import { useSession } from "@/lib/auth-client";
import { formatCurrency } from "@/lib/formatters";

interface GrSovTabProps {
  projectId: string;
}

interface LineFormValues {
  itemNo: string;
  description: string;
  costCodeId: string;
  scheduledValue: string;
  retentionPercentOverride: string;
}

const DEFAULT_FORM: LineFormValues = {
  itemNo: "",
  description: "",
  costCodeId: "",
  scheduledValue: "",
  retentionPercentOverride: "",
};

export function GrSovTab({ projectId }: GrSovTabProps) {
  const { data: session } = useSession();
  const role = session?.user?.role;
  const canEdit = role === "admin" || role === "project_manager";

  const sovQ = useGrSov(projectId);
  const varianceQ = useGrVariance(projectId);
  const costCodesQ = useCostCodes();
  const commitmentsQ = useCommitments({ projectId });
  const saveMutation = useUpdateGrSov(projectId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editLine, setEditLine] = useState<GrSovLine | null>(null);

  const { register, handleSubmit, reset, setValue, watch } = useForm<LineFormValues>({
    defaultValues: DEFAULT_FORM,
  });

  const watchedCostCode = watch("costCodeId");

  // Owner prime commitment for new line creation (accept either legacy type)
  const ownerPrime = commitmentsQ.data?.find(
    (c) => c.commitmentType === "prime_contract" || c.commitmentType === "owner_contract",
  );

  function openAdd() {
    setEditLine(null);
    reset(DEFAULT_FORM);
    setDialogOpen(true);
  }

  function openEdit(line: GrSovLine) {
    setEditLine(line);
    reset({
      itemNo: line.itemNo ?? "",
      description: line.description,
      costCodeId: line.costCodeId ?? "",
      scheduledValue: line.scheduledValue ?? "",
      retentionPercentOverride: line.retentionPercentOverride ?? "",
    });
    setDialogOpen(true);
  }

  function onSubmit(values: LineFormValues) {
    if (!ownerPrime) return;
    const input: GrSovLineInput = {
      ...(editLine ? { id: editLine.id } : {}),
      commitmentId: editLine?.commitmentId ?? ownerPrime.id,
      itemNo: values.itemNo.trim(),
      description: values.description.trim(),
      costCodeId: values.costCodeId,
      scheduledValue: values.scheduledValue,
      ...(values.retentionPercentOverride.trim()
        ? { retentionPercentOverride: values.retentionPercentOverride }
        : {}),
    };
    saveMutation.mutate([input], {
      onSuccess: () => setDialogOpen(false),
    });
  }

  const lines = sovQ.data ?? [];
  const varianceMap = new Map(
    (varianceQ.data ?? []).map((v) => [
      v.id,
      {
        approvedTotal: Number(v.approvedTotal),
        remaining: Number(v.remaining),
        isOverBudget: v.isOverBudget,
      },
    ]),
  );

  const totalScheduled = lines.reduce((s, l) => s + Number(l.scheduledValue ?? 0), 0);
  const totalApproved = (varianceQ.data ?? []).reduce((s, v) => s + Number(v.approvedTotal), 0);

  if (sovQ.isLoading || varianceQ.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (sovQ.isError) {
    return <ErrorState message={sovQ.error?.message} onRetry={() => sovQ.refetch()} />;
  }

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <h3 className="text-base font-semibold">General Requirements SOV</h3>
          <p className="text-sm text-muted-foreground">
            GR line items billed on the owner prime contract
          </p>
        </div>
        {canEdit ? (
          <Button size="sm" onClick={openAdd} disabled={!ownerPrime}>
            <Plus className="mr-1 h-4 w-4" />
            Add GR Line
          </Button>
        ) : null}
      </div>

      {!ownerPrime && canEdit ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No owner prime contract found for this project. Create one on the Buy Log tab before
          adding GR SOV lines.
        </div>
      ) : null}

      {/* Summary metrics */}
      {lines.length > 0 ? (
        <div className="grid grid-cols-3 gap-3">
          <MetricCard label="Scheduled Value" value={formatCurrency(totalScheduled)} />
          <MetricCard label="Approved to Date" value={formatCurrency(totalApproved)} />
          <MetricCard
            label="Remaining"
            value={formatCurrency(totalScheduled - totalApproved)}
            warn={totalApproved > totalScheduled}
          />
        </div>
      ) : null}

      {/* Table */}
      {lines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <p className="text-sm text-gray-500">
            No GR SOV lines yet.{" "}
            {canEdit ? 'Click "Add GR Line" to define General Requirements line items.' : ""}
          </p>
        </div>
      ) : (
        <div className="rounded-xl border bg-white">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Item #
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Description
                </TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Cost Code
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Scheduled Value
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Approved Total
                </TableHead>
                <TableHead className="text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Remaining
                </TableHead>
                {canEdit ? <TableHead className="w-10" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => {
                const v = varianceMap.get(line.id);
                const costCode = costCodesQ.data?.find((c) => c.id === line.costCodeId);
                return (
                  <TableRow key={line.id}>
                    <TableCell className="font-mono text-sm">{line.itemNo ?? "—"}</TableCell>
                    <TableCell>{line.description}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {costCode ? `${costCode.code} — ${costCode.description}` : "—"}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(Number(line.scheduledValue ?? 0))}
                    </TableCell>
                    <TableCell className="text-right">
                      {v ? formatCurrency(v.approvedTotal) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {v ? (
                        <span
                          className={
                            v.isOverBudget ? "font-semibold text-red-600" : "text-green-700"
                          }
                        >
                          {v.isOverBudget ? (
                            <TriangleAlert className="mr-1 inline h-3.5 w-3.5" />
                          ) : null}
                          {formatCurrency(Math.abs(v.remaining))}
                          {v.isOverBudget ? " over" : ""}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    {canEdit ? (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEdit(line)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[50vw]">
          <DialogHeader>
            <DialogTitle>{editLine ? "Edit GR SOV Line" : "Add GR SOV Line"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Item #</Label>
                <Input placeholder="GR-01" {...register("itemNo", { required: true })} />
              </div>
              <div className="space-y-1.5">
                <Label>Scheduled Value</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  {...register("scheduledValue", { required: true })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                placeholder="e.g. Dumpster Rental, Porta-Potties"
                {...register("description", { required: true })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Cost Code</Label>
                <Select value={watchedCostCode} onValueChange={(v) => setValue("costCodeId", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select cost code…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(costCodesQ.data ?? []).map((cc) => (
                      <SelectItem key={cc.id} value={cc.id}>
                        {cc.code} — {cc.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Retention Override %</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="Leave blank to inherit"
                  {...register("retentionPercentOverride")}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Saving…" : editLine ? "Save Changes" : "Add Line"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Sub-components ──

function MetricCard({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${warn ? "border-red-200 bg-red-50" : "bg-white"}`}>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${warn ? "text-red-700" : ""}`}>{value}</p>
      {warn ? (
        <Badge variant="destructive" className="mt-1 text-xs">
          Over Budget
        </Badge>
      ) : null}
    </div>
  );
}
